import type { SessionRow } from '@matter_extractor/shared';

/**
 * What the existing screens of Sessions say of the REVIEW (relecture.md §3.2,
 * §13, issue #187) - pure, and nothing of the review page itself: the button
 * that leads to it, the mark of the list and the ninth filter.
 *
 * ## Why it lives apart from the page's own logic
 *
 * The page (selection, marking, tabs) is written by another lot, and these four
 * small rules are the only things the parcours, the conversation and the list
 * need to know of it. They read the session ROW and nothing else: the review is
 * not fetched to draw a button.
 */

/** The ways the entry to the review shows on the head of a session (§3.1). */
export type ReviewEntryState =
  /** A frozen experience with no protocol page: nothing to range, so no button. */
  | 'absent'
  /** The session is still running: the button exists, really disabled, and says why. */
  | 'disabled'
  | 'open';

/** What the row says of a session: only its status and its protocol decide (§3.1). */
export type ReviewEntrySession = Pick<SessionRow, 'status' | 'protocolId'>;

/**
 * Terminated, closed or expired AND a protocol page in the frozen experience
 * opens the review; a session in progress shows the button disabled; a frozen
 * experience without a protocol page has no button at all.
 *
 * A session in the trash keeps its entry: its review stays READABLE there (§3.4),
 * the page itself refuses the gestures.
 */
export function reviewEntryState(session: ReviewEntrySession): ReviewEntryState {
  if (session.protocolId === null) return 'absent';
  return session.status === 'in-progress' ? 'disabled' : 'open';
}

/** The sentence under the disabled button (§3.1). */
export const REVIEW_ENTRY_REASON = 'La relecture s’ouvre quand la session est finie.';

/* ------------------------------------------------------------- the list */

/** « patrick » out of `patrick@patrickmathieu.net`; a name with no `@` (« jeton partagé ») stays whole. */
export function reviewAuthorName(author: string): string {
  const at = author.indexOf('@');
  return at > 0 ? author.slice(0, at) : author;
}

/**
 * « relecture · patrick, alice » - who set something in the review of a session
 * (§13), the local parts of the emails in the order the server gave them. Two
 * addresses with one local part read once. Null when nobody did: no mark.
 */
export function reviewMark(authors: readonly string[]): string | null {
  const names: string[] = [];
  for (const author of authors) {
    const name = reviewAuthorName(author);
    if (!names.includes(name)) names.push(name);
  }
  return names.length === 0 ? null : `relecture · ${names.join(', ')}`;
}

/** The two values of the ninth filter, as the address bar and the server spell them. */
export type ReviewFilter = 'with' | 'without';

/** The chip of the filter once armed: « Relecture : avec ». */
export function reviewFilterChipLabel(filter: ReviewFilter): string {
  return `Relecture : ${filter === 'with' ? 'avec' : 'sans'}`;
}

/** What the selector offers, in the voice of the maquette `retouches`. */
export const REVIEW_FILTER_OPTION_LABEL: Readonly<Record<ReviewFilter, string>> = {
  with: 'Avec une relecture',
  without: 'Sans relecture',
};
